import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import '../../../registerSyncfusion';
import { ScheduleComponent, ViewsDirective, ViewDirective, Day, Week, WorkWeek, Month, Inject } from '@syncfusion/ej2-react-schedule';
import { L10n, loadCldr, setCulture } from '@syncfusion/ej2-base';
import { selectEntitiesByFilters } from '../../../store/views/entityView';
import { eventsBySectionsQuery } from '../../../utils/entitiesFiltersUtils';
import { mapEventToCalendar } from '../../../utils/utils';
import { hebrewDateType } from '../../../enums/hebrewDateType';
import { calendarType } from '../../../enums/calendarType';
import * as caGregorian from './jsons/ca-gregorian.json';
import * as caHebrew from './jsons/ca-hebrew.json';
import * as numberingSystems from './jsons/numberingSystems.json';
import * as numbers from './jsons/numbers.json';
import * as timeZoneNames from './jsons/timeZoneNames.json';
import moment from 'moment';
import { AccessibilityTabContent } from '@skillz/accessibility';
import scheduleTexts from './jsons/schedule.json';
import recurrenceeditorTexts from './jsons/recurrenceeditor.json';
import Filters from '../../ui/filters/Filters';
import DateHeaderTemplate from './dropTemplates/DateHeaderTemplate';
import MonthEventTemplate from './dropTemplates/MonthEventTemplate';
import DayEventTemplate from './dropTemplates/DayEventTemplate';
import WeekEventTemplate from './dropTemplates/WeekEventTemplate';
import quickInfoTemplates from './dropTemplates/QuickInfoTemplates';
import classes from './FullCalendar.module.css';
import pathRouts from '../../../enums/pathRoute';
import "./FullCalender.css";
import useHebrewDate from '../../../hooks/useHebrewDate ';
import useInstance from '../../../hooks/useInstance';
import endPointsSections from '../../../store/models/endPointsSections';

const loadLocalizationData = () => {

    loadCldr(
        caGregorian,
        caHebrew,
        numberingSystems,
        numbers,
        timeZoneNames
    );

    L10n.load({
        'he': {
            "schedule": scheduleTexts.he,
            "recurrenceeditor": recurrenceeditorTexts.he
        },
        'ar': {
            "schedule": scheduleTexts.ar,
            "recurrenceeditor": recurrenceeditorTexts.ar
        }
    });
};

loadLocalizationData();

const CalenderPage = () => {
    const { userDetails } = useSelector(state => state.auth);
    const schoolId = userDetails?.defaultSchool?.id;
    const myEventsQuery = useMemo(() => eventsBySectionsQuery(endPointsSections.calendar, schoolId), [schoolId]);
    const eventsFromRedux = useSelector((state) => selectEntitiesByFilters(state, myEventsQuery));
    const events = useMemo(() => eventsFromRedux?.map(mapEventToCalendar), [eventsFromRedux]);
    const { texts, lang } = useSelector(state => state.translate);
    const { providers, domains, general } = useSelector(state => state.uiData)
    const deviceType = useSelector((state) => state.responsive.deviceType);
    const [filters, setFilters] = useState([]);
    const [workHours, setWorkHours] = useState({ highlight: true, start: general?.startCalendarTime, end: general?.endCalendarTime });
    const url = new URL(window.location.href);
    const selectedDate = url.searchParams.get("selectedDate");
    const parsedSelectedDate = selectedDate ? new Date(selectedDate) : new Date();
    const dispatch = useDispatch();
    const hebrewDate = useHebrewDate();
    const instance = useInstance();
    const calendarMode = window.config.calendarMode;

    // פונקציה שמחזירה את ה־template עם כל הערכים הדרושים
    const dateHeaderTemplate = (props) => (
        <DateHeaderTemplate
            {...props}
            instance={instance}
            hebrewDate={hebrewDate}
            calendarMode={calendarMode}
        />
    );

    const scheduleRef = useRef(null);

    useEffect(() => {
        setCulture(lang);
    }, [lang])

    const scrollToCurrentTime = () => {
        const scheduleObj = scheduleRef.current;
        const now = new Date();
        const nowHour = now.getHours();
        const nowMinutes = now.getMinutes();

        if (nowHour && nowMinutes) {
            scheduleObj.scrollTo(`${nowHour}:00`);
        }
    };

    useEffect(() => {
        if (general)
            scrollToCurrentTime();
    }, [general, workHours]);

    // useEffect(() => {
    //     if (deviceType !== deviceTypes.desktop)
    //         dispatch(uiDataActions.updateShowSideTabs(false))
    //     else
    //         dispatch(uiDataActions.updateShowSideTabs(true))
    // }, [dispatch, deviceType])

    useEffect(() => {
        if (general)
            setWorkHours({ highlight: true, start: general?.startCalendarTime, end: general?.endCalendarTime });
    }, [general]);

    useEffect(() => {
        if (providers && domains) {
            setFilters([
                {
                    name: "Domain",
                    text: texts.areasInterest,
                    expanded: false,
                    values: domains.map(domain => ({
                        name: domain.name,
                        text: domain.text,
                        color: domain.color,
                        check: false
                    })),
                },
                {
                    name: "ProviderName",
                    text: texts.studying,
                    expanded: false,
                    values: providers.map(provider => ({
                        name: provider.name,
                        text: provider.text,
                        color: provider.color,
                        check: false
                    }))
                }
            ])
        }
    }, [providers, domains, texts])

    const filterEvents = (events) => {
        if (filters.some(filter => filter.values.some(value => value.check))) {
            return events.filter(event => filters.every(filter =>
                filter.values.some(value => value.check) ?
                    filter.values.map(value => {
                        if ((event[filter.name] === value.name && value.check) || event.Domain === "holidays")
                            return true;
                        return false;
                    }).some(value => value) :
                    true
            ))
        }
        else {
            return events;
        }
    }

    const timeScale = { enable: true, interval: 60, slotCount: 1 };


    const onRenderCell = (args) => {
        if (args?.elementType === "workCells" || args?.elementType === "monthCells" || args?.elementType === "alldayCells")
            (args?.element)?.classList.add(moment(args?.date).format("DD.MM.YY"))

        if (window.config.calendarMode === calendarType.jewish)
            if (args?.element && args?.elementType === "monthCells")
                (args.element).innerHTML = hebrewDate(args.date, hebrewDateType.day)
    }

    const onEventRendered = (args) => {
        if (args?.data?.holiday)
            Array.from(document.getElementsByClassName(moment(args?.data?.date).format("DD.MM.YY"))).forEach((ele) => { ele.style.background = "#E7F5F9"; });
    }
    const onCellClick = (args) => {
        const scheduleObj = scheduleRef.current;
        if (scheduleObj && scheduleObj.currentView === "Month") {
            scheduleObj.selectedDate = args.startTime;
            scheduleObj.changeView("Day");
        }
    };
    return (
        <AccessibilityTabContent id={pathRouts.CALENDAR} hidden={false}>
            {events &&
                <div className={classes.CalendarContainer}>
                    {/* <div className={classes.ProviderFilter} id="main">
                        <Filters
                            listboxId={"fullCalendarFilters"}
                            setFilters={setFilters}
                            filters={filters}
                            collapse
                            border />
                    </div> */}
                    {general && <ScheduleComponent
                        ref={scheduleRef}
                        height='100%'
                        timeScale={timeScale}
                        quickInfoTemplates={quickInfoTemplates}
                        showQuickInfo={false}
                        dateHeaderTemplate={dateHeaderTemplate}
                        enableRtl={true}
                        delayUpdate='true'
                        className={classes.CustomCalendar}
                        workHours={workHours}
                        selectedDate={parsedSelectedDate}
                        startHour={general?.startCalendarTime}
                        endHour={general?.endCalendarTime}
                        currentView="Month"
                        cellClick={onCellClick}
                        workDays={[0, 1, 2, 3, 4, 5, 6]}
                        eventSettings={{
                            dataSource: filterEvents(events),
                            allowAdding: false,
                            allowDeleting: false,
                            allowEditing: false
                        }}
                        renderCell={onRenderCell}
                        eventRendered={onEventRendered}
                        locale={lang}
                    >

                        <ViewsDirective>
                            <ViewDirective option="Day" eventTemplate={DayEventTemplate} dateHeaderTemplate={dateHeaderTemplate} />
                            <ViewDirective option="Week" eventTemplate={WeekEventTemplate} dateHeaderTemplate={dateHeaderTemplate} />
                            {/* <ViewDirective option="Month" /> */}
                            <ViewDirective option="Month" eventTemplate={MonthEventTemplate} />
                        </ViewsDirective>
                        <Inject services={[Day, Week, WorkWeek, Month]} />
                        <Inject styles={[`.e-schedule .e-day-header { height: 40px; }`]} />
                    </ScheduleComponent>}
                </div>}
        </AccessibilityTabContent >
    );
};

export default CalenderPage;